import type { EngineRuleRoomRate } from '@niarab2c/frontend-commons/src/types/hotels';
import type { Card, PayLaterOptionByRule, PaymentOption, setPayLaterOptionByRule, ShoppingCartState } from '../shoppingCart';
import type { AddonAvailabilityResult } from '@niarab2c/frontend-commons/src/types/addon';
import _isEqual from 'lodash/isEqual';
const mergePaymentOptionsAccumulate = (paymentOptions: PaymentOption[], roomRatePaymentOptions: EngineRuleRoomRate['paymentOptions'] | AddonAvailabilityResult['paymentOptions'], itemIndex?: number, itemType?: PaymentOption['paidItems'][number]['type'], createPayLater?: boolean, payLaterOptionByRule?: PayLaterOptionByRule): PaymentOption[] => {
  const _buildCards = (p: PaymentOption): PaymentOption => {
    if (p.oncc_conditions) {
      const paymentValue = p.paymentValue ?? 0;
      p.oncc_conditions = p.oncc_conditions.map(oncc => {
        const maxInstRule = oncc.maxInstRule ?? 1;
        const minInstValue = oncc.minInstValue ?? 0;
        const maxInst = minInstValue > 0 ? Math.min(maxInstRule, Math.floor(Math.round(paymentValue * 100) / Math.round(minInstValue * 100))) : maxInstRule;
        return {
          ...oncc,
          maxInstRule,
          minInstValue,
          maxInst
        };
      });
      const cards: Array<Card> = [];
      p.oncc_conditions.forEach(item => {
        const {
          cardBrands,
          maxInst,
          minInstValue,
          maxInstRule
        } = item;
        cardBrands.map(brand => {
          cards.push({
            brand: brand,
            maximumInstallments: maxInst,
            maxInst: maxInst,
            minInstValue,
            maxInstRule,
            description: `(em até ${maxInst} vez${maxInst > 1 ? 'es' : ''})`
          });
        });
      });
      p.cards = cards;
    }
    return p;
  };
  const itemPaymentOptions = roomRatePaymentOptions?.map<PaymentOption>(p2 => {
    if (itemIndex === undefined) {
      // link de pagamento, por exemplo - manter o paidItems do p2
      return {
        ...p2
      };
    }
    return {
      ...p2,
      paidItems: [{
        itemIndex,
        amount: p2.paymentValue,
        currency: p2.paymentCurrency,
        recurringPayments: p2.recurringPayments,
        type: itemType
      }],
      loyaltyRedeemPaidItems: p2.loyaltyRedeemId ? [{
        itemIndex,
        amount: p2.pointsWorth,
        currency: p2.paymentCurrency,
        type: itemType,
        loyaltyRedeemId: p2.loyaltyRedeemId
      }] : undefined
    };
  });

  // adiciona a opção de pagar depois se for reservar -> pagar feat #164342
  if (createPayLater) {
    const defaultPayLaterAlias = "Pagar depois";
    itemPaymentOptions.push({
      type: 'LATER',
      id: 'LATER',
      alias: payLaterOptionByRule?.payLaterAlias ?? defaultPayLaterAlias,
      description: payLaterOptionByRule?.payLaterDescription ?? null,
      loyaltyRedeemPaidItems: itemPaymentOptions?.find(r => r.loyaltyRedeemPaidItems)?.loyaltyRedeemPaidItems,
      // assume que todos os paymentOptions possuem o mesmo loyaltyRedeemPaidItems
      enabled: true,
      paidItems: [] // não faz sentido pro pagar depois
    });
  }
  if (!paymentOptions) {
    return itemPaymentOptions?.map(_buildCards);
  } else {
    return paymentOptions.map(prevPaymentOption => {
      const itemPaymentOption = itemPaymentOptions.find(p => p.id === prevPaymentOption.id && (p.paymentCurrency === prevPaymentOption.paymentCurrency || p.currency === prevPaymentOption.currency));
      if (!itemPaymentOption) {
        // novo item não aceita esse tipo de pagamento
        return null;
      } else {
        const paymentValue = prevPaymentOption.paymentValue + itemPaymentOption.paymentValue || null;
        const total = prevPaymentOption.total + itemPaymentOption.total || null;
        // TODO como juntar oncc_conditions com diferentes maxInstRule? #201892

        let oncc_conditions = prevPaymentOption.oncc_conditions;
        if (oncc_conditions) {
          oncc_conditions = prevPaymentOption.oncc_conditions.map(prevOncc => {
            // new oncc
            const newOncc = itemPaymentOption.oncc_conditions?.find(c => _isEqual(c.cardBrands, prevOncc.cardBrands));
            if (!newOncc) return null; //algo errado!
            const maxInstRule = Math.min(prevOncc.maxInstRule ?? 1, newOncc.maxInstRule ?? 1);
            const minInstValue = Math.min(prevOncc.minInstValue ?? 1, newOncc.minInstValue ?? 1);
            return {
              ...prevOncc,
              maxInstRule,
              minInstValue
            };
          }).filter(x => x);
        }
        const paidItems = [...(prevPaymentOption.paidItems || []), ...(itemPaymentOption.paidItems || [])];
        const allRecurringPayments = [...(prevPaymentOption?.recurringPayments || []), ...(itemPaymentOption?.recurringPayments || [])]?.filter(Boolean);
        const recurringPayments = allRecurringPayments?.reduce((acc, recurringPayment) => {
          const existingRecurringPaymentIndex = acc.findIndex(r => r.date?.substring(0, 10) == recurringPayment.date?.substring(0, 10));
          if (existingRecurringPaymentIndex >= 0) {
            const v = acc[existingRecurringPaymentIndex];
            acc[existingRecurringPaymentIndex] = {
              ...v,
              value: v.value + recurringPayment.value
            };
            return acc;
          } else {
            return [...acc, recurringPayment];
          }
        }, [] as ShoppingCartState['paymentOptions'][number]['recurringPayments']);
        return {
          ...prevPaymentOption,
          paymentValue,
          total,
          loyaltyRedeemPaidItems: [...(prevPaymentOption.loyaltyRedeemPaidItems || []), ...(itemPaymentOption.loyaltyRedeemPaidItems || [])],
          oncc_conditions,
          paidItems,
          recurringPayments
        };
      }
    }).filter(Boolean).map(_buildCards);
  }
};
type PartialAddon = Pick<EngineRuleRoomRate['addons'][number], 'paymentOptions'>;
type PartialRoomRate = Pick<EngineRuleRoomRate, 'paymentOptions' | 'checkoutStrategy' | 'laterHasSupplierPayment'> & {
  addons?: PartialAddon[];
};
export const mergePaymentOptionsCart = (cartItems: ReadonlyArray<{
  roomRates: [PartialRoomRate];
}>, payLaterOptionByRule: PayLaterOptionByRule, b2c?: boolean): PaymentOption[] => {
  let paymentOptions: PaymentOption[] | null = null;
  let paidItemIndex = -1;
  cartItems?.forEach(cartItem => {
    const roomRate = cartItem.roomRates[0];
    const createPayLater = !b2c && roomRate.checkoutStrategy === 'BOOKING_THEN_PAYMENT' && roomRate?.laterHasSupplierPayment;
    paymentOptions = mergePaymentOptionsAccumulate(paymentOptions, roomRate.paymentOptions, ++paidItemIndex, 'hotelReservation', createPayLater, payLaterOptionByRule);
    if (roomRate?.addons?.[0]) {
      const addon = roomRate.addons[0]; // TODO - resolver itemIndex quando tem múltiplos extras
      paymentOptions = mergePaymentOptionsAccumulate(paymentOptions, addon.paymentOptions ?? [], ++paidItemIndex, 'addon', createPayLater, payLaterOptionByRule);
    }
  });
  return paymentOptions;
};
type MergePaymentOptions = {
  (cartItems: ReadonlyArray<{
    roomRates: [PartialRoomRate];
  }>, payLaterOptionByRule: PayLaterOptionByRule, b2c?: boolean): PaymentOption[];
  (paymentOptions: PaymentOption[], roomRatePaymentOptions: EngineRuleRoomRate['paymentOptions'] | AddonAvailabilityResult['paymentOptions'], itemIndex: number, itemType: PaymentOption['paidItems'][number]['type'], createPayLater: boolean, payLaterOptionByRule: PayLaterOptionByRule): PaymentOption[];
  /**
   * para link de pagamentos
   */
  (paymentOptions: null, roomRatePaymentOptions: EngineRuleRoomRate['paymentOptions'] | AddonAvailabilityResult['paymentOptions']): PaymentOption[];
};
const mergePaymentOptions: MergePaymentOptions = (...params) => {
  if (params && params[1] != null && typeof params[1] == 'object' && Array.isArray(params[1])) {
    return mergePaymentOptionsAccumulate(...(params as Parameters<typeof mergePaymentOptionsAccumulate>));
  } else {
    return mergePaymentOptionsCart(...(params as Parameters<typeof mergePaymentOptionsCart>));
  }
};
export default mergePaymentOptions;